import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface MailerLite {
  _id: string;
  apiKey?: string;
}

export function SettingsMailerLite() {
  const [mailerLite, setMailerLite] = useState<MailerLite | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingMailerLite, setIsCreatingMailerLite] = useState(false);
  const [isDeletingMailerLite, setIsDeletingMailerLite] = useState(false);
  const [isSavingMailerLite, setIsSavingMailerLite] = useState(false);
  const [showMailerLiteApiKey, setShowMailerLiteApiKey] = useState(false);
  const [mailerLiteApiKey, setMailerLiteApiKey] = useState("");

  useEffect(() => {
    async function fetchMailerLite() {
      setIsLoading(true);
      try {
        const mailerLiteResponse = await fetch("/api/mailerlite");
        if (mailerLiteResponse.ok) {
          const mailerLiteData = await mailerLiteResponse.json();
          if (mailerLiteData.mailerLite) {
            setMailerLite(mailerLiteData.mailerLite);
            setMailerLiteApiKey(mailerLiteData.mailerLite.apiKey || "");
          }
        } else if (mailerLiteResponse.status !== 404) {
          console.error("Failed to fetch MailerLite integration");
        }
      } catch (error) {
        console.error("Failed to fetch MailerLite integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMailerLite();
  }, []);

  const handleCreateMailerLite = async () => {
    setIsCreatingMailerLite(true);
    try {
      const response = await fetch("/api/mailerlite", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mailerLite) {
          setMailerLite(data.mailerLite);
          setMailerLiteApiKey(data.mailerLite.apiKey || "");
          toast.success("MailerLite integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create MailerLite integration");
      }
    } catch (error) {
      console.error("Error creating MailerLite integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create MailerLite integration");
    } finally {
      setIsCreatingMailerLite(false);
    }
  };

  const handleSaveMailerLite = async () => {
    if (!mailerLite) return;

    setIsSavingMailerLite(true);
    try {
      const response = await fetch("/api/mailerlite", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: mailerLiteApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mailerLite) {
          setMailerLite(data.mailerLite);
          toast.success("MailerLite settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save MailerLite settings");
      }
    } catch (error) {
      console.error("Error saving MailerLite settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save MailerLite settings");
    } finally {
      setIsSavingMailerLite(false);
    }
  };

  const handleDeleteMailerLite = async () => {
    if (!mailerLite) return;

    if (!confirm("Are you sure you want to delete your MailerLite integration?")) {
      return;
    }

    setIsDeletingMailerLite(true);
    try {
      const response = await fetch(`/api/mailerlite/${mailerLite._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setMailerLite(null);
        setMailerLiteApiKey("");
        toast.success("MailerLite integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete MailerLite integration");
      }
    } catch (error) {
      console.error("Error deleting MailerLite integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete MailerLite integration");
    } finally {
      setIsDeletingMailerLite(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">MailerLite</h3>
            <p className="text-sm text-muted-foreground">
              Connect your MailerLite account to manage email subscribers
            </p>
            {mailerLite && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {!mailerLite ? (
            <Button onClick={handleCreateMailerLite} disabled={isCreatingMailerLite}>
              {isCreatingMailerLite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          ) : (
            <Button onClick={handleDeleteMailerLite} disabled={isDeletingMailerLite} variant="destructive">
              {isDeletingMailerLite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        {mailerLite && (
          <div className="space-y-2 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="mailerLiteApiKey">API Key</Label>
              <div className="relative">
                <Input
                  id="mailerLiteApiKey"
                  type={showMailerLiteApiKey ? "text" : "password"}
                  value={mailerLiteApiKey}
                  onChange={(e) => setMailerLiteApiKey(e.target.value)}
                  placeholder="Enter MailerLite API key"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowMailerLiteApiKey(!showMailerLiteApiKey)}
                >
                  {showMailerLiteApiKey ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            <Button onClick={handleSaveMailerLite} disabled={isSavingMailerLite}>
              {isSavingMailerLite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
